import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, swatchUrlExists } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {string | null} */
  #cachedProductUrl = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get selectedOptionValues() {
    return this.dataset.optionValues || '';
  }

  get productPageUrl() {
    const productCardLink = /** @type {HTMLAnchorElement | null} */ (
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')
    );

    return (
      productCardLink?.href ||
      this.dataset.productUrl
    );
  }

  get requestCacheKey() {
    return [this.productPageUrl || '', this.selectedOptionValues].join('::');
  }

  /**
   * The dialog this component owns.
   *
   * Scoped to the component rather than resolved with `document.getElementById`: the dialog is
   * always a child of `quick-add-component` in all four markup sources (`product-card.liquid`,
   * `nosto-product-addon-card.liquid`, `nosto-listing.js`, `predictive-search.js.liquid`), and
   * an id lookup would open the wrong card's dialog on any page rendering the same product
   * twice — a grid plus a Nosto recommendation carousel, for instance. Falls back to the id so
   * any markup that nests differently keeps working.
   *
   * @returns {QuickAddDialog | null}
   */
  get dialogComponent() {
    const scoped = this.querySelector('quick-add-dialog');
    if (scoped instanceof QuickAddDialog) return scoped;

    const byId = document.getElementById('quick-add-dialog-' + this.dataset.productId);
    return byId instanceof QuickAddDialog ? byId : null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const dialogComponent = this.dialogComponent;
    if (!dialogComponent) return;

    // Fast path — content for this exact product + option set is already cached, so render and
    // open in the same frame. No loading state, and no flash of the previous selection before
    // the reset that `updateQuickAddModal` performs.
    if (this.#cachedProductHtml && this.requestCacheKey === this.#cachedProductUrl) {
      await this.updateQuickAddModal(this.#freshCachedHtml(), dialogComponent);
      this.#openQuickAddModal(dialogComponent);
      return;
    }

    // Slow path — open first, so the tap always gets immediate visual feedback. Previously the
    // dialog only appeared once a full network round trip had resolved, with nothing on screen
    // in between, which reads as a dead button and provokes repeat taps. This mirrors
    // `modal-availability.js`, which already opens then fetches.
    dialogComponent.renderLoadingState();
    this.#openQuickAddModal(dialogComponent);

    // Alpine writes `data-product-url` / `data-option-values` reactively when a card swatch
    // changes, so wait a frame for the settled values before deciding what to request. This
    // used to sit ahead of the dialog opening, delaying first paint by a frame for no benefit.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const currentProductUrl = this.productPageUrl;
    const currentRequestCacheKey = this.requestCacheKey;
    const shouldRefetchProduct = !this.#cachedProductHtml || currentRequestCacheKey !== this.#cachedProductUrl;

    if (shouldRefetchProduct) {
      const status = await this.fetchProductPage(currentProductUrl);

      // A newer click aborted this request; that click renders in its own turn.
      if (status === 'aborted') return;

      if (status === 'error') {
        dialogComponent.renderErrorState(currentProductUrl);
        return;
      }
    }

    if (this.#cachedProductHtml) {
      await this.updateQuickAddModal(this.#freshCachedHtml(), dialogComponent);
    }
  };

  /**
   * A throwaway copy of the cached document, so rendering never mutates the cache.
   * @returns {Document}
   */
  #freshCachedHtml() {
    return new DOMParser().parseFromString(this.#cachedProductHtml.documentElement.outerHTML, 'text/html');
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /** @param {QuickAddDialog} [dialogComponent] */
  #openQuickAddModal = (dialogComponent = this.dialogComponent) => {
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = this.dialogComponent;
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content.
   *
   * Returns a status rather than throwing: the dialog is already open by the time this runs, so
   * the caller needs to decide what to render. Previously a failed fetch produced an unhandled
   * rejection and the dialog simply never opened.
   *
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<'ok' | 'aborted' | 'error'>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return 'error';

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const requestUrl = new URL(productPageUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', 'section-rendering-product-card');
      if (this.selectedOptionValues) {
        requestUrl.searchParams.set('option_values', this.selectedOptionValues);
      }

      const response = await fetch(requestUrl.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
      this.#cachedProductUrl = this.requestCacheKey;

      return 'ok';
    } catch (error) {
      if (error.name === 'AbortError') return 'aborted';

      console.warn('Unable to load Quick Add content.', error);
      return 'error';
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @param {QuickAddDialog | null} [dialogComponent] - The dialog to render into.
   */
  async updateQuickAddModal(newHtml, dialogComponent = this.dialogComponent) {
    const quickViewContent = newHtml.querySelector('[data-quick-view-content]');
    const modalContentContainer = dialogComponent?.querySelector('[data-quick-view-modal-content]');

    if (!quickViewContent || !modalContentContainer) return;

    modalContentContainer.innerHTML = quickViewContent.outerHTML;
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.setBusy(false);

      const insertedContent = modalContentContainer.querySelector('[data-quick-view-content]');
      void dialogComponent.syncFileSwatchVisibility(insertedContent);
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/**
 * Reads a theme translation, falling back to English.
 *
 * `Theme` is declared with `const` in a classic script (`snippets/scripts.liquid`), so it lives
 * in the global lexical environment and is *not* reachable via `globalThis` — hence the bare
 * identifier behind a `typeof` guard.
 *
 * @param {string} key - The key under `Theme.translations`.
 * @param {string} fallback - Text to use if the key is missing.
 * @returns {string}
 */
function translate(key, fallback) {
  if (typeof Theme === 'undefined') return fallback;

  return Theme?.translations?.[key] || fallback;
}

/**
 * @param {string} value
 * @returns {string} `value` with HTML-significant characters escaped.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * Placeholder shown the instant the dialog opens, while the product content is fetched.
 *
 * Deliberately cheap: opening the dialog now happens inside the click's own frame, so whatever
 * this renders is what that interaction has to paint. Every class used here is already present
 * in the compiled `assets/main.css`, so this needs no Tailwind rebuild.
 *
 * @returns {string}
 */
function loadingMarkup() {
  const label = escapeHtml(translate('quick_add_title', 'Quick add to bag'));

  return `
    <div class="flex h-full min-h-0 flex-col bg-white px-5 py-5" data-quick-view-loading>
      <span class="sr-only" role="status">${label}</span>
      <div class="flex h-full min-h-0 flex-col" aria-hidden="true">
        <div class="h-3 w-1/3 rounded-sm bg-gray-200 motion-safe:animate-pulse"></div>
        <div class="mt-6 aspect-product-image w-full rounded-sm bg-gray-200 motion-safe:animate-pulse"></div>
        <div class="mt-6 space-y-2">
          <div class="h-[15px] w-2/3 rounded-sm bg-gray-200 motion-safe:animate-pulse"></div>
          <div class="h-[15px] w-1/3 rounded-sm bg-gray-200 motion-safe:animate-pulse"></div>
        </div>
        <div class="mt-auto h-12 w-full rounded-sm bg-gray-200 motion-safe:animate-pulse"></div>
      </div>
    </div>
  `;
}

/**
 * Shown when the product content can't be fetched. The dialog is already open by then, so it
 * needs something to say rather than sitting on a skeleton forever.
 *
 * @param {string} productUrl - Fallback link target.
 * @returns {string}
 */
function errorMarkup(productUrl) {
  const message = escapeHtml(translate('quick_add_error', 'Something went wrong, please try again.'));
  const linkLabel = escapeHtml(translate('view_more_details', 'View more details'));
  const href = escapeHtml(productUrl || '');

  return `
    <div class="flex h-full min-h-0 flex-col items-center justify-center gap-4 px-5 py-5 text-center" data-quick-view-error>
      <p class="body-regular text-muted-black" role="status">${message}</p>
      ${href ? `<a class="body-regular text-muted-black underline" href="${href}">${linkLabel}</a>` : ''}
    </div>
  `;
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** @returns {Element | null} The container the fetched quick-view content renders into. */
  get contentContainer() {
    return this.querySelector('[data-quick-view-modal-content]');
  }

  /**
   * Marks the dialog busy while content is in flight.
   * @param {boolean} isBusy
   */
  setBusy(isBusy) {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLElement)) return;

    if (isBusy) dialog.setAttribute('aria-busy', 'true');
    else dialog.removeAttribute('aria-busy');
  }

  /**
   * Renders the placeholder shown while the product content loads. Idempotent, so repeat taps
   * during a slow fetch cost one write, not one per tap.
   */
  renderLoadingState() {
    const container = this.contentContainer;
    if (!(container instanceof HTMLElement)) return;
    if (container.firstElementChild?.hasAttribute('data-quick-view-loading')) return;

    container.innerHTML = loadingMarkup();
    this.setBusy(true);
  }

  /**
   * Renders the failure state, with a link through to the product page.
   * @param {string} productUrl
   */
  renderErrorState(productUrl) {
    const container = this.contentContainer;
    if (!(container instanceof HTMLElement)) return;

    container.innerHTML = errorMarkup(productUrl);
    this.setBusy(false);
  }

  #getClosestActionTarget(event, selector) {
    const target = event.target;

    if (target instanceof Element) {
      return target.closest(selector);
    }

    const actionTarget = event.composedPath().find((node) => node instanceof Element && node.matches(selector));
    return actionTarget instanceof Element ? actionTarget : null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.cartAdd, this.handleCartAdd, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleDialogClick, { signal: this.#abortController.signal });
    this.addEventListener('change', this.#handleDialogChange, { signal: this.#abortController.signal });
    void this.syncFileSwatchVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  handleCartAdd = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  async syncFileSwatchVisibility(content = this.querySelector('[data-quick-view-modal-content] [data-quick-view-content]')) {
    if (!(content instanceof HTMLElement)) return;

    const tasks = [
      ...Array.from(content.querySelectorAll('[data-quick-view-file-swatch-label]')).map(async (label) => {
        if (!(label instanceof HTMLElement)) return;

        const image = label.querySelector('[data-quick-view-file-swatch-image]');
        if (!(image instanceof HTMLImageElement)) return;

        const exists = await swatchUrlExists(image.currentSrc || image.getAttribute('src') || '');
        label.classList.toggle('hidden', !exists);
      }),
      ...Array.from(content.querySelectorAll('[data-quick-view-option-swatch-frame]')).map(async (frame) => {
        if (!(frame instanceof HTMLElement)) return;

        const image = frame.querySelector('[data-quick-view-option-swatch-image]');
        if (!(image instanceof HTMLImageElement)) return;

        const exists = await swatchUrlExists(image.currentSrc || image.getAttribute('src') || '');
        frame.classList.toggle('hidden', !exists);
        frame.classList.toggle('inline-flex', exists);
      }),
    ];

    await Promise.all(tasks);
  }

  #handleVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const nextContent = event.detail.data.html?.querySelector('[data-quick-view-content]');
    const currentContent = this.querySelector('[data-quick-view-modal-content] [data-quick-view-content]');

    if (!nextContent || !currentContent) return;

    const selectedOptions = this.#getSelectedOptions(currentContent);

    morph(currentContent, nextContent);

    const matchedVariant = this.#getMatchedVariant(currentContent, selectedOptions);
    if (!matchedVariant) return;

    this.#restoreSelectedOptions(currentContent, selectedOptions);
    this.#syncVariantUi(currentContent, matchedVariant, selectedOptions);
    void this.syncFileSwatchVisibility(currentContent);
  };

  #handleDialogChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('input[type="radio"][data-input-id]')) return;

    const content = event.target.closest('[data-quick-view-content]');
    if (!(content instanceof HTMLElement)) return;

    const selectedOptions = this.#getSelectedOptions(content);
    const matchedVariant = this.#getMatchedVariant(content, selectedOptions);
    if (!matchedVariant) return;

    this.#syncVariantUi(content, matchedVariant, selectedOptions);
    void this.syncFileSwatchVisibility(content);
  };

  #handleDialogClick = (event) => {
    const showAllSwatchesButton = this.#getClosestActionTarget(event, '[data-show-all-swatches], [data-show-all-colours]');
    if (showAllSwatchesButton) {
      event.preventDefault();
      event.stopPropagation();
      const content = showAllSwatchesButton.closest('[data-quick-view-content]');
      content?.querySelectorAll('[data-extra-swatch], [data-extra-colour]').forEach((element) => {
        element.classList.remove('hidden');
        element.removeAttribute('hidden');
        if (element instanceof HTMLElement) {
          element.style.removeProperty('display');
        }
      });
      showAllSwatchesButton.classList.add('hidden');
      showAllSwatchesButton.setAttribute('hidden', 'hidden');
      return;
    }

    const optionButton = this.#getClosestActionTarget(event, '[data-quick-view-option-button]');
    if (!(optionButton instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#selectOptionVariant(optionButton);
  };

  #getQuickViewVariants(content) {
    const variantsScript = content?.querySelector('script[type="application/json"][data-quick-view-variants]');
    if (!(variantsScript instanceof HTMLScriptElement)) return [];

    try {
      const parsed = JSON.parse(variantsScript.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Failed to parse quick view variants JSON', error);
      return [];
    }
  }

  #getSelectedOptions(content) {
    const selectedOptions = {};

    content.querySelectorAll('input[type="radio"][data-input-id]:checked').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const optionPosition = input.dataset.optionPosition || input.dataset.inputId?.split('-')[0];
      const optionValue = input.dataset.optionValue || input.value;

      if (!optionPosition || !optionValue) return;
      selectedOptions[optionPosition] = optionValue;
    });

    content.querySelectorAll('[data-quick-view-option-button][data-selected="true"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (!button.dataset.optionPosition || !button.dataset.optionValue) return;
      selectedOptions[button.dataset.optionPosition] = button.dataset.optionValue;
    });

    return selectedOptions;
  }

  #getMatchedVariant(content, selectedOptions = this.#getSelectedOptions(content)) {
    const variants = this.#getQuickViewVariants(content);
    if (!variants.length) return null;

    return (
      variants.find((variant) =>
        Object.entries(selectedOptions).every(([position, value]) => !value || variant[`option${position}`] === value)
      ) || null
    );
  }

  #restoreSelectedOptions(content, selectedOptions) {
    content.querySelectorAll('input[type="radio"][data-input-id]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const optionPosition = input.dataset.optionPosition || input.dataset.inputId?.split('-')[0];
      const optionValue = input.dataset.optionValue || input.value;

      if (!optionPosition || !optionValue) return;
      input.checked = selectedOptions[optionPosition] === optionValue;
    });
  }

  #syncVariantUi(content, matchedVariant, selectedOptions) {
    const variantInput = content.querySelector('[ref="variantId"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = matchedVariant.id;
      if (!matchedVariant.available) {
        variantInput.setAttribute('disabled', 'disabled');
      } else {
        variantInput.removeAttribute('disabled');
      }
    }

    const priceContainer = content.querySelector('[data-quick-view-price]');
    if (priceContainer instanceof HTMLElement && matchedVariant.priceHtml) {
      priceContainer.innerHTML = matchedVariant.priceHtml;
    }

    const swatchValue = content.querySelector('[data-quick-view-primary-swatch-value]');
    if (swatchValue instanceof HTMLElement) {
      swatchValue.textContent = selectedOptions['1'] || matchedVariant.option1 || '';
    }

    this.#applySelectedOptionState(content, matchedVariant);

    const statusType = matchedVariant.statusType || '';
    const statusIcon = matchedVariant.statusIcon;
    const statusDetail = content.querySelector('[data-quick-view-status-detail]');
    if (statusDetail instanceof HTMLElement) {
      statusDetail.textContent =
        statusType === 'out_of_stock'
          ? matchedVariant.statusText || matchedVariant.statusDetail || ''
          : matchedVariant.statusDetail || '';
    }

    const statusIconContainer = content.querySelector('[data-quick-view-status-icon]');
    if (statusIconContainer instanceof HTMLElement) {
      statusIconContainer.querySelectorAll('[data-status-icon-option]').forEach((icon) => {
        const isActive = icon.dataset.statusIconOption === statusIcon;
        icon.classList.toggle('hidden', !isActive);
        icon.classList.toggle('inline-flex', isActive);
      });

      statusIconContainer.classList.toggle('text-[#A60505]', statusType === 'out_of_stock');
      statusIconContainer.classList.toggle('text-black', statusType !== 'out_of_stock');
    }

    const statusPill = content.querySelector('[data-quick-view-status-pill]');
    if (statusPill instanceof HTMLElement) {
      statusPill.className = statusType === 'out_of_stock'
        ? 'inline-flex min-h-9 items-center gap-2 rounded-full bg-pale-red px-4 py-2 alt-body-tiny text-black'
        : 'flex min-h-6 w-full items-center justify-center gap-3 alt-body-tiny leading-none text-muted-black';
    }

    if (statusDetail instanceof HTMLElement) {
      statusDetail.classList.toggle('truncate', statusType !== 'out_of_stock');
      statusDetail.classList.toggle('text-center', statusType !== 'out_of_stock');
    }

    const standardActions = content.querySelector('[data-quick-view-actions-standard]');
    if (standardActions instanceof HTMLElement) {
      standardActions.classList.toggle('hidden', statusType === 'out_of_stock');
    }

    const outOfStockActions = content.querySelector('[data-quick-view-actions-oos]');
    if (outOfStockActions instanceof HTMLElement) {
      outOfStockActions.classList.toggle('hidden', statusType !== 'out_of_stock');
    }

    // Keep the "Notify me" link pointed at the selected variant so the PDP opens
    // on the right variant and auto-opens the notify dialog.
    const notifyButton = content.querySelector('[data-quick-view-notify-button]');
    if (notifyButton instanceof HTMLAnchorElement) {
      const notifyUrl = new URL(notifyButton.href, window.location.origin);
      notifyUrl.searchParams.set('variant', matchedVariant.id);
      notifyUrl.searchParams.set('notify_me', '1');
      notifyButton.href = notifyUrl.toString();
    }

    const addToCartButton = content.querySelector('[ref="addToCartButton"]');
    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !matchedVariant.available;
    }
  }

  #applySelectedOptionState(content, variant) {
    content.querySelectorAll('[data-quick-view-option-button]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const optionPosition = button.dataset.optionPosition;
      const optionValue = button.dataset.optionValue;
      const isSelected = optionPosition && optionValue && variant?.[`option${optionPosition}`] === optionValue;

      button.classList.toggle('border-black', !!isSelected);
      button.classList.toggle('bg-secondary', !!isSelected);
      button.classList.toggle('border-gray-300', !isSelected);
      button.classList.toggle('bg-white', !isSelected);
      button.setAttribute('data-selected', isSelected ? 'true' : 'false');

      const swatchFrame = button.querySelector('[data-quick-view-option-swatch-frame]');
      if (swatchFrame instanceof HTMLElement) {
        swatchFrame.classList.toggle('border-black', !!isSelected);
        swatchFrame.classList.toggle('border-transparent', !isSelected);
      }
    });
  }

  #findMatchingVariant(variants, selectedOptions, changedOptionPosition, changedOptionValue) {
    const exactMatch = variants.find((variant) =>
      Object.entries(selectedOptions).every(([position, value]) => !value || variant[`option${position}`] === value)
    );

    if (exactMatch) return exactMatch;

    const matchingChangedOption = variants.filter(
      (variant) => variant[`option${changedOptionPosition}`] === changedOptionValue
    );

    return matchingChangedOption.find((variant) => variant.available) || matchingChangedOption[0] || null;
  }

  #selectOptionVariant(optionButton) {
    const content = optionButton.closest('[data-quick-view-content]');
    if (!(content instanceof HTMLElement)) return;

    const optionPosition = optionButton.dataset.optionPosition;
    const optionValue = optionButton.dataset.optionValue;
    if (!optionPosition || !optionValue) return;

    const variants = this.#getQuickViewVariants(content);
    if (!variants.length) return;

    const selectedOptions = this.#getSelectedOptions(content);

    selectedOptions[optionPosition] = optionValue;

    const matchedVariant = this.#findMatchingVariant(variants, selectedOptions, optionPosition, optionValue);
    if (!matchedVariant) return;

    this.#syncVariantUi(content, matchedVariant, selectedOptions);
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
